/* eslint-disable max-lines */
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons/lib'
import { Divider, Row } from 'antd'
import CategorySegmentListItem from 'app/components/Core/CategorySegment/ListItem'
import CheckoutDrawer from 'app/components/Core/Checkout/Drawer'
import { checkoutFooterPortalId } from 'app/components/Core/Checkout/Footer'
import { useProductDelete } from 'app/components/Core/Checkout/hooks/useProduct'
import Form from 'app/components/Core/LineItem/Form'
import SubItemEditableList from 'app/components/Core/SubItem/EditableList'
import KButton from 'app/components/Modules/General/KButton'
import Button from 'app/components/UI-Elements/General/Button'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import KFooter from 'app/components/UI-Elements/Layout/KFooter'
import { RecordType } from 'app/constants/GlobalTypes'
import { normalizeProductSubItems } from 'app/lib/apollo/normalizers/LineItem'
import { useFeedbacker } from 'app/lib/contexts/Feedbacker'
import { useLazyQuery } from 'app/lib/hooks/useQuery'
import useTranslation from 'app/lib/hooks/useTranslation'
import Packages from 'app/pages/Checkout/Container/LineItem/Packages'
import { queries } from 'app/queries'
import React, { useEffect, useState } from 'react'
import testIds from 'test_ids.json'

interface Props {
  lineItemId: number
  initialPage?: 'sub_items' | 'packages' | null
  type?: 'update_rental' | 'checkout'
  onSuccess?: () => void
}
const packagesListDrawerKey = 'packages_list_drawer_key'
const subItemsListDrawerKey = 'sub_items_list_drawer_key'

const LineItemUpdate: React.FC<Props> = ({
  lineItemId,
  initialPage,
  onSuccess,
  type = 'checkout'
}) => {
  const { tRecordType } = useTranslation({
    recordType: RecordType.package
  })
  const { tRecordType: tRecordTypeLineItem } = useTranslation({
    recordType: RecordType.line_item
  })

  const [packagesVisible, setPackagesVisible] = useState(false)
  const [subItemsVisible, setSubItemsVisible] = useState(false)
  const { drawerfy } = useFeedbacker()
  const { execute: executeDelete, loading } = useProductDelete()

  const {
    modalify: { open }
  } = useFeedbacker()

  const {
    data: { lineItem },
    refetch: refetchLineItem,
    execute
  } = useLazyQuery<{ lineItem: ILineItem }>(queries.lineItem.get, {
    normalizer: (data) => ({ lineItem: normalizeProductSubItems(data.lineItem) }),
    // do not refetch item on destroy
    nextFetchPolicy: 'cache-only'
  })
  const isCheckout = type === 'checkout'

  useEffect(() => {
    if (initialPage === 'packages') {
      onAdd()
    }
    if (initialPage === 'sub_items') {
      setSubItemsVisible(true)
    }
    execute({
      variables: {
        id: lineItemId,
        includeCategoryAttributes: true,
        includeSubItems: true
      }
    })
  }, [])

  const onFormSuccess = () => {
    onSuccess?.()
    drawerfy.close()
  }

  const onDelete = () => {
    open({
      recordType: RecordType.line_item,
      action: 'destroy',
      titleTranslationValues: { name: lineItem?.name },
      modalType: 'confirm',
      onConfirm: async () => {
        await executeDelete({ id: lineItem.id })
        drawerfy.close()
        onSuccess?.()
      }
    })
  }

  const onSubItemUpdateSuccess = () => {
    setPackagesVisible(false)
    onSuccess?.()
  }

  const onSubItemCreateSuccess = () => {
    refetchLineItem()
    onSubItemUpdateSuccess()
  }

  const onAdd = () => {
    if (subItemsVisible) {
      setSubItemsVisible(false)
      setTimeout(() => setPackagesVisible(true), 200)
    } else {
      setPackagesVisible(true)
    }
  }

  const closeDrawers = () => {
    setPackagesVisible(false)
    setTimeout(drawerfy.close, 200)
  }

  const packagesFooter = (
    <KFooter sticky={false}>
      <KButton
        onClick={closeDrawers}
        recordType={RecordType.package}
        action="skip"
        title={tRecordType({ id: 'skip' })} // double check which translation is used. We have 3 "Zur Maschinenübersicht"
        type="primary"
      />
    </KFooter>
  )

  const subItemsFooter = (
    <KFooter sticky={false}>
      <KButton
        onClick={closeDrawers}
        recordType={RecordType.package}
        action="skip"
        title={tRecordType({ id: 'skip' })}
        type="primary"
        hidden={!isCheckout}
      />
    </KFooter>
  )

  const SubItems = React.useMemo(
    () => (
      <Row gutter={[0, 24]}>
        <KCol span={24}>
          <SubItemEditableList
            collection={lineItem?.subItems}
            props={{
              showDelete: isCheckout,
              onUpdateSuccess: onSubItemUpdateSuccess
            }}
          />
        </KCol>
        <KCol span={24} className="text-center" hidden={!isCheckout}>
          <KButton
            icon={<PlusCircleOutlined />}
            type="secondary"
            recordType={RecordType.line_item}
            action="add_attachments"
            onClick={onAdd}
            data-testid={testIds.checkout.machines.sub_item.add_button} // TODO
            title={tRecordTypeLineItem({ id: 'add_attachments' })}
          />
        </KCol>
      </Row>
    ),
    [lineItem]
  )

  return (
    <Row>
      <KCol span={24} loading={!lineItem}>
        <If condition={lineItem}>
          <Row gutter={[0, 24]}>
            <KCol span={24}>
              <CategorySegmentListItem
                member={lineItem.categorySegment}
                props={{
                  title: <span className="h4">{lineItem?.name}</span>,
                  extra: isCheckout && (
                    <Button
                      type="secondary"
                      onClick={onDelete}
                      loading={loading}
                      icon={<DeleteOutlined />}
                    />
                  )
                }}
              />
            </KCol>
            <KCol span={24}>
              <Form
                member={lineItem}
                props={{
                  onSuccess: onFormSuccess,
                  portalId: checkoutFooterPortalId,
                  showAttributeValues: isCheckout,
                  visualAction: 'save_changes'
                }}>
                <If condition={lineItem.categorySegment.hasAttachments}>
                  <Row gutter={[0, 12]}>
                    <If condition={lineItem.subItems?.length}>
                      <KCol span={24}>
                        <h4 className="text-black mb-0">
                          {tRecordTypeLineItem({ id: 'attachments' })}
                        </h4>
                      </KCol>
                      <KCol span={24}>
                        <Divider className="my-0" />
                      </KCol>
                    </If>
                    <KCol span={24}>{SubItems}</KCol>
                  </Row>
                </If>
              </Form>
            </KCol>
          </Row>
          <CheckoutDrawer
            title={tRecordTypeLineItem(
              { id: 'sub_items_with_values' },
              {
                name: lineItem.categorySegment.name
              }
            )}
            drawerKey={packagesListDrawerKey}
            open={packagesVisible}
            onClose={() => setPackagesVisible(false)}
            footer={packagesFooter}>
            <Packages parentItem={lineItem} onSubItemCreate={onSubItemCreateSuccess} />
          </CheckoutDrawer>
          <CheckoutDrawer
            title={tRecordTypeLineItem(
              { id: 'sub_items_with_values' },
              {
                name: lineItem.categorySegment.name
              }
            )}
            drawerKey={subItemsListDrawerKey}
            open={subItemsVisible}
            onClose={() => setSubItemsVisible(false)}
            footer={subItemsFooter}>
            {SubItems}
          </CheckoutDrawer>
        </If>
      </KCol>
    </Row>
  )
}

export default LineItemUpdate
